import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import ImportantTextCell from 'src/components/table/cell-types/ImportantTextCell';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {listPageTaskTemplatesQuery} from './__generated__/listPageTaskTemplatesQuery.graphql';
import {TaskTemplatesOrder} from './__generated__/TaskTemplatesPaginationQuery.graphql';
import {TaskTemplatesTable_taskTemplates$key} from './__generated__/TaskTemplatesTable_taskTemplates.graphql';
import {TaskTemplatesTableDeleteTaskTemplateMutation} from './__generated__/TaskTemplatesTableDeleteTaskTemplateMutation.graphql';
import TableFilters from './TableFilters';

type Props = {
  taskTemplates: TaskTemplatesTable_taskTemplates$key;
};

type Filters = {
  name?: string;
  orderBy?: TaskTemplatesOrder;
};

function TaskTemplatesTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageTaskTemplatesQuery,
    TaskTemplatesTable_taskTemplates$key
  >(
    graphql`
      fragment TaskTemplatesTable_taskTemplates on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "TaskTemplatesOrder"}
        )
        @refetchable(queryName: "TaskTemplatesPaginationQuery") {
        taskTemplates(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "TaskTemplatesList_taskTemplates") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              builtIn
              taskKind
              name
            }
          }
        }
      }
    `,
    props.taskTemplates,
  );

  const [commit] = useMutation<TaskTemplatesTableDeleteTaskTemplateMutation>(
    deleteTaskTemplateMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.taskTemplates.totalCount}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      isTransitioning={isTransitioning}
      onDelete={handleDelete}
      data={data.taskTemplates.edges}
      columns={columns}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {taskTemplateId: id}},
      onCompleted: payload => {
        const entry = payload.deleteTaskTemplate?.taskTemplate;

        if (entry) {
          showEventNotification('removed', 'Template', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'TaskTemplatesList_taskTemplates',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default TaskTemplatesTable;

const deleteTaskTemplateMutation = graphql`
  mutation TaskTemplatesTableDeleteTaskTemplateMutation($input: DeleteTaskTemplateInput!) {
    deleteTaskTemplate(input: $input) {
      taskTemplate {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
  {
    Header: 'Task Type',
    accessor: 'node.taskKind',
    id: 'taskKind',
    Cell({cell: {value}}: any) {
      return <ImportantTextCell value={value} />;
    },
  },
];
